import {
  isGatewayRequestError,
  isRetryableGatewayError,
  retryAfterMsFromError,
} from "../gateway-errors.ts";

const MAX_SEND_RETRIES = 2;
const BASE_BACKOFF_MS = 1_000;
const MAX_BACKOFF_MS = 30_000;

export function sleepMs(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Race a promise against a wall-clock timeout. If the promise does not
 * settle before `ms`, reject with an Error carrying `message`.
 *
 * Used by the chat composer to cap chat.send RPCs that would otherwise
 * hang forever when the gateway accepts but never responds (server
 * crash before runId assigned, network black hole, hung backend). The
 * caller treats the timeout as a normal retryable error so the existing
 * retry / display-error path is reused — no special-casing needed.
 */
export function withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | null = null;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error(message)), ms);
  });
  return Promise.race([promise, timeout]).finally(() => {
    if (timer != null) {
      clearTimeout(timer);
      timer = null;
    }
  });
}

function jitterMs(baseMs: number): number {
  const jitter = Math.floor(Math.random() * Math.min(500, baseMs * 0.25));
  return baseMs + jitter;
}

export function computeRetryDelayMs(err: unknown, attemptIndex: number): number {
  const hinted = retryAfterMsFromError(err);
  if (hinted) {
    return Math.min(MAX_BACKOFF_MS, jitterMs(hinted));
  }
  const exponential = Math.min(MAX_BACKOFF_MS, BASE_BACKOFF_MS * 2 ** attemptIndex);
  return jitterMs(exponential);
}

export function shouldRetryChatSend(err: unknown, attemptIndex: number): boolean {
  if (attemptIndex >= MAX_SEND_RETRIES) {
    return false;
  }
  return isRetryableGatewayError(err);
}

export function formatGatewayErrorForDisplay(err: unknown): string {
  if (isGatewayRequestError(err)) {
    return err.message;
  }
  if (err instanceof Error) {
    return err.message;
  }
  return String(err ?? "request failed");
}

export const CHAT_SEND_MAX_RETRIES = MAX_SEND_RETRIES;
